import { useState } from "react";
import { useSearch } from "wouter";

import { useAuth } from "../auth/AuthContext.js";

/**
 * Callback page for the device CLI authentication flow.
 *
 * URL: /auth/device-cli?callback=http://127.0.0.1:<port>/callback
 *
 * Shows a confirmation screen. When the user clicks "Authorize", calls
 * the platform API to create a fresh CLI session token, then redirects
 * to the callback URL with it.
 */
export function DeviceCliCallback() {
  const { user, loading } = useAuth();
  const search = useSearch();
  const callbackUrl = new URLSearchParams(search).get("callback");
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState("");

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </main>
    );
  }

  if (!callbackUrl) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-red-400">Missing callback URL parameter</p>
      </main>
    );
  }

  let parsedCallback: URL;
  try {
    parsedCallback = new URL(callbackUrl);
  } catch {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-red-400">Invalid callback URL</p>
      </main>
    );
  }

  const isLocalhost =
    parsedCallback.hostname === "127.0.0.1" || parsedCallback.hostname === "localhost";

  if (!user) return null;

  async function handleAuthorize() {
    setError("");
    setRedirecting(true);

    try {
      // Create a fresh CLI session token via the platform API
      const apiUrl = window.serverConfig?.API_URL || window.location.origin;
      const res = await fetch(`${apiUrl}/api/v1/admin/auth/cli-token`, {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`Failed to create CLI token (HTTP ${res.status})`);
      }

      const data = (await res.json()) as { session_token: string };
      const url = new URL(callbackUrl!);
      url.searchParams.set("session_token", data.session_token);
      window.location.href = url.toString();
    } catch (e) {
      setRedirecting(false);
      setError((e as Error).message);
    }
  }

  if (redirecting) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-text-secondary">Redirecting to device CLI...</p>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-bold text-text-primary">Device CLI Authorization</h1>
          <p className="mt-2 text-text-secondary">
            The TeamTable device CLI is requesting access to your admin session.
          </p>
        </div>

        <div className="bg-surface-raised border border-border rounded-lg p-4 space-y-3">
          <div>
            <p className="text-sm text-text-muted">Logged in as</p>
            <p className="text-text-primary font-medium">{user.display_name}</p>
            <p className="text-sm text-text-secondary">{user.email}</p>
          </div>

          <div>
            <p className="text-sm text-text-muted">Callback URL</p>
            <p className="text-sm text-text-secondary font-mono break-all">{callbackUrl}</p>
          </div>

          {!isLocalhost && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
              <p className="text-sm text-red-400 font-medium">
                Warning: This callback is not on localhost. Only authorize if you trust this URL.
              </p>
            </div>
          )}
        </div>

        {error && <p className="text-center text-sm text-red-400">{error}</p>}

        <div className="space-y-3">
          <button
            onClick={handleAuthorize}
            className="w-full rounded-lg bg-accent px-4 py-3 font-medium text-white hover:bg-accent-hover transition-colors"
          >
            Authorize Device CLI
          </button>
          <button
            onClick={() => window.close()}
            className="w-full rounded-lg border border-border px-4 py-3 text-text-secondary hover:text-text-primary hover:border-text-muted transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </main>
  );
}
